import { createElement } from 'react';

import { TeeIcon } from '../../assets/icons/tee.tsx';
import { type ContourMarkerItem } from '../map-marker/index.ts';
import {
  type CourseContourPolygon,
  type CourseMapData,
  type CourseZonePolygon,
  CourseZonePolygonType,
  type MapLatLng,
} from './course-map-types.ts';

export type CourseMapIGolfGeometryPoint = {
  lat: number;
  lon: number;
};

export type CourseMapIGolfGeometryShape = {
  points: CourseMapIGolfGeometryPoint[];
};

export type CourseMapIGolfHoleGeometry = {
  green?: CourseMapIGolfGeometryShape[];
  fairway?: CourseMapIGolfGeometryShape[];
  perimeter?: CourseMapIGolfGeometryShape[];
  bunkers?: CourseMapIGolfGeometryShape[];
  teeboxes?: CourseMapIGolfGeometryShape[];
  central_path?: CourseMapIGolfGeometryShape[];
  green_center?: CourseMapIGolfGeometryShape[];
  teebox_centers?: CourseMapIGolfGeometryShape[];
};

export type CourseMapIGolfHole = {
  id: number | string;
  hole_number: number;
  geometry: CourseMapIGolfHoleGeometry;
  tee_points?: unknown;
  device_point?: CourseMapDevicePoint | null;
};

export type CourseMapDevicePoint = {
  lat: number;
  lon: number;
};

export type CourseMapTeePointDropZone = {
  id: string;
  center: CourseMapDevicePoint;
  contour: MapLatLng[] | null;
};

export type CourseMapIGolfCourse = {
  id_course: string;
  course_name: string;
  latitude?: string | number | null;
  longitude?: string | number | null;
  layout_holes?: number | null;
  holes: CourseMapIGolfHole[];
};

export type BuildCourseHoleMapDataParams = {
  courseId: string;
  courseName?: string;
  hole: CourseMapIGolfHole;
};

type GeometryPolygonZoneKey = 'green' | 'fairway' | 'perimeter' | 'bunkers' | 'teeboxes';

type BoundsBox = {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
};

const GEOMETRY_ZONE_TYPE_MAP: Array<[GeometryPolygonZoneKey, CourseZonePolygonType]> = [
  ['green', CourseZonePolygonType.GREEN],
  ['teeboxes', CourseZonePolygonType.TEEBOX],
  ['fairway', CourseZonePolygonType.FAIRWAY],
  ['bunkers', CourseZonePolygonType.BUNKER],
];

const TEE_POINT_CENTER_SNAP_RADIUS = 0.0001;
const POINT_ON_CONTOUR_RADIUS = 0.00000001;

const point = (lat: number, lng: number): MapLatLng => ({ lat, lng });

const toFiniteNumber = (value: unknown) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const getFirstRecordValue = (record: Record<string, unknown>, keys: string[]): unknown => {
  const targetKey = keys.find((key) => record[key] !== undefined);

  return targetKey ? record[targetKey] : undefined;
};

const toDevicePoint = (latValue: unknown, lonValue: unknown): CourseMapDevicePoint | null => {
  const lat = toFiniteNumber(latValue);
  const lon = toFiniteNumber(lonValue);

  if (lat === null || lon === null) return null;
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;

  return { lat, lon };
};

const toMapPointFromDevicePoint = (devicePoint: CourseMapDevicePoint): MapLatLng => {
  return point(devicePoint.lat, devicePoint.lon);
};

const isValidGeometryPoint = (geometryPoint: CourseMapIGolfGeometryPoint): boolean => {
  return Number.isFinite(geometryPoint.lat) && Number.isFinite(geometryPoint.lon);
};

const toMapPoint = (geometryPoint: CourseMapIGolfGeometryPoint): MapLatLng => {
  return point(geometryPoint.lat, geometryPoint.lon);
};

const normalizeContour = (contour: MapLatLng[]): MapLatLng[] => {
  if (contour.length < 3) return contour;

  const first = contour[0];
  const last = contour[contour.length - 1];

  if (first.lat === last.lat && first.lng === last.lng) {
    return contour.slice(0, -1);
  }

  return contour;
};

const extractContoursFromGeometryShapes = (
  shapes: CourseMapIGolfGeometryShape[] | undefined
): MapLatLng[][] => {
  return (shapes ?? [])
    .map((shape) => shape.points.filter(isValidGeometryPoint).map(toMapPoint))
    .map(normalizeContour)
    .filter((contour) => contour.length >= 3);
};

const extractPointsFromGeometryShapes = (
  shapes: CourseMapIGolfGeometryShape[] | undefined
): MapLatLng[] => {
  return (shapes ?? []).flatMap((shape) =>
    shape.points.filter(isValidGeometryPoint).map(toMapPoint)
  );
};

const getFirstPointFromGeometryShapes = (
  shapes: CourseMapIGolfGeometryShape[] | undefined
): MapLatLng | null => {
  for (const shape of shapes ?? []) {
    const firstPoint = shape.points.find(isValidGeometryPoint);

    if (firstPoint) {
      return toMapPoint(firstPoint);
    }
  }

  return null;
};

const getBoundsBox = (points: MapLatLng[]): BoundsBox | null => {
  if (!points.length) return null;

  return points.reduce<BoundsBox>(
    (acc, currentPoint) => ({
      minLat: Math.min(acc.minLat, currentPoint.lat),
      maxLat: Math.max(acc.maxLat, currentPoint.lat),
      minLng: Math.min(acc.minLng, currentPoint.lng),
      maxLng: Math.max(acc.maxLng, currentPoint.lng),
    }),
    {
      minLat: points[0].lat,
      maxLat: points[0].lat,
      minLng: points[0].lng,
      maxLng: points[0].lng,
    }
  );
};

const getBoundsCenter = (bounds: BoundsBox): MapLatLng => {
  return point((bounds.minLat + bounds.maxLat) / 2, (bounds.minLng + bounds.maxLng) / 2);
};

const getSquaredDistance = (first: MapLatLng, second: MapLatLng): number => {
  const deltaLat = first.lat - second.lat;
  const deltaLng = first.lng - second.lng;

  return deltaLat * deltaLat + deltaLng * deltaLng;
};

const getDistanceToSegmentSquared = (
  samplePoint: MapLatLng,
  segmentStart: MapLatLng,
  segmentEnd: MapLatLng
): number => {
  const segmentLengthSquared = getSquaredDistance(segmentStart, segmentEnd);

  if (segmentLengthSquared === 0) {
    return getSquaredDistance(samplePoint, segmentStart);
  }

  const projectionFactor =
    ((samplePoint.lat - segmentStart.lat) * (segmentEnd.lat - segmentStart.lat) +
      (samplePoint.lng - segmentStart.lng) * (segmentEnd.lng - segmentStart.lng)) /
    segmentLengthSquared;
  const clampedProjectionFactor = Math.max(0, Math.min(1, projectionFactor));
  const projectedPoint = point(
    segmentStart.lat + (segmentEnd.lat - segmentStart.lat) * clampedProjectionFactor,
    segmentStart.lng + (segmentEnd.lng - segmentStart.lng) * clampedProjectionFactor
  );

  return getSquaredDistance(samplePoint, projectedPoint);
};

const getDistanceToContourSquared = (samplePoint: MapLatLng, contour: MapLatLng[]): number => {
  let closestDistanceSquared = Number.POSITIVE_INFINITY;

  contour.forEach((currentPoint, index) => {
    const nextPoint = contour[(index + 1) % contour.length];
    const distanceSquared = getDistanceToSegmentSquared(samplePoint, currentPoint, nextPoint);

    if (distanceSquared < closestDistanceSquared) {
      closestDistanceSquared = distanceSquared;
    }
  });

  return closestDistanceSquared;
};

const isPointInsideContour = (samplePoint: MapLatLng, contour: MapLatLng[]): boolean => {
  let isInside = false;

  for (
    let currentIndex = 0, previousIndex = contour.length - 1;
    currentIndex < contour.length;
    previousIndex = currentIndex++
  ) {
    const currentPoint = contour[currentIndex];
    const previousPoint = contour[previousIndex];
    const intersects =
      currentPoint.lng > samplePoint.lng !== previousPoint.lng > samplePoint.lng &&
      samplePoint.lat <
        ((previousPoint.lat - currentPoint.lat) * (samplePoint.lng - currentPoint.lng)) /
          (previousPoint.lng - currentPoint.lng) +
          currentPoint.lat;

    if (intersects) {
      isInside = !isInside;
    }
  }

  return isInside;
};

const isPointInsideOrOnContour = (samplePoint: MapLatLng, contour: MapLatLng[]): boolean => {
  return (
    isPointInsideContour(samplePoint, contour) ||
    getDistanceToContourSquared(samplePoint, contour) <=
      POINT_ON_CONTOUR_RADIUS * POINT_ON_CONTOUR_RADIUS
  );
};

const getContourArea = (contour: MapLatLng[]): number => {
  if (contour.length < 3) return 0;

  const areaTimesTwo = contour.reduce((acc, currentPoint, index) => {
    const nextPoint = contour[(index + 1) % contour.length];

    return acc + currentPoint.lng * nextPoint.lat - nextPoint.lng * currentPoint.lat;
  }, 0);

  return Math.abs(areaTimesTwo) / 2;
};

const getContourCentroid = (contour: MapLatLng[]): MapLatLng | null => {
  if (contour.length < 3) return null;

  let areaTimesTwo = 0;
  let latSum = 0;
  let lngSum = 0;

  contour.forEach((currentPoint, index) => {
    const nextPoint = contour[(index + 1) % contour.length];
    const cross = currentPoint.lng * nextPoint.lat - nextPoint.lng * currentPoint.lat;

    areaTimesTwo += cross;
    lngSum += (currentPoint.lng + nextPoint.lng) * cross;
    latSum += (currentPoint.lat + nextPoint.lat) * cross;
  });

  if (Math.abs(areaTimesTwo) < Number.EPSILON) {
    const bounds = getBoundsBox(contour);

    return bounds ? getBoundsCenter(bounds) : null;
  }

  return point(latSum / (3 * areaTimesTwo), lngSum / (3 * areaTimesTwo));
};

const getLargestContour = (contours: MapLatLng[][]): MapLatLng[] | null => {
  let largestContour: MapLatLng[] | null = null;
  let largestArea = 0;

  contours.forEach((contour) => {
    const area = getContourArea(contour);

    if (area > largestArea) {
      largestArea = area;
      largestContour = contour;
    }
  });

  return largestContour;
};

const VISUAL_CENTER_GRID_DIVISIONS = 32;
const VISUAL_CENTER_REFINEMENT_STEPS = 3;

const getVisualCenterFromContour = (contour: MapLatLng[]): MapLatLng | null => {
  if (contour.length < 3) return null;

  const bounds = getBoundsBox(contour);

  if (!bounds) {
    return null;
  }

  const centroid = getContourCentroid(contour);
  let bestPoint =
    centroid && isPointInsideContour(centroid, contour) ? centroid : getBoundsCenter(bounds);
  let bestDistanceSquared = isPointInsideContour(bestPoint, contour)
    ? getDistanceToContourSquared(bestPoint, contour)
    : 0;
  let searchBounds = bounds;

  for (let stepIndex = 0; stepIndex < VISUAL_CENTER_REFINEMENT_STEPS; stepIndex += 1) {
    const latSpan = searchBounds.maxLat - searchBounds.minLat;
    const lngSpan = searchBounds.maxLng - searchBounds.minLng;
    const latStep = latSpan / VISUAL_CENTER_GRID_DIVISIONS;
    const lngStep = lngSpan / VISUAL_CENTER_GRID_DIVISIONS;

    for (let latIndex = 0; latIndex <= VISUAL_CENTER_GRID_DIVISIONS; latIndex += 1) {
      const sampleLat = searchBounds.minLat + latStep * latIndex;

      for (let lngIndex = 0; lngIndex <= VISUAL_CENTER_GRID_DIVISIONS; lngIndex += 1) {
        const sampleLng = searchBounds.minLng + lngStep * lngIndex;
        const samplePoint = point(sampleLat, sampleLng);

        if (!isPointInsideContour(samplePoint, contour)) {
          continue;
        }

        const distanceSquared = getDistanceToContourSquared(samplePoint, contour);

        if (distanceSquared > bestDistanceSquared) {
          bestDistanceSquared = distanceSquared;
          bestPoint = samplePoint;
        }
      }
    }

    const latRadius = Math.max(latSpan / 4, 0.000001);
    const lngRadius = Math.max(lngSpan / 4, 0.000001);

    searchBounds = {
      minLat: Math.max(bounds.minLat, bestPoint.lat - latRadius),
      maxLat: Math.min(bounds.maxLat, bestPoint.lat + latRadius),
      minLng: Math.max(bounds.minLng, bestPoint.lng - lngRadius),
      maxLng: Math.min(bounds.maxLng, bestPoint.lng + lngRadius),
    };
  }

  return bestPoint;
};

const getCenterFromContours = (contours: MapLatLng[][]): MapLatLng | null => {
  const bounds = getBoundsBox(contours.flat());

  return bounds ? getBoundsCenter(bounds) : null;
};

const getLargestContourVisualCenterFromGeometryShapes = (
  shapes: CourseMapIGolfGeometryShape[] | undefined
): MapLatLng | null => {
  const largestContour = getLargestContour(extractContoursFromGeometryShapes(shapes));

  return largestContour ? getVisualCenterFromContour(largestContour) : null;
};

const getCenterFromGeometryShapes = (
  shapes: CourseMapIGolfGeometryShape[] | undefined
): MapLatLng | null => {
  return getCenterFromContours(extractContoursFromGeometryShapes(shapes));
};

const getFirstContourCenterFromGeometryShapes = (
  shapes: CourseMapIGolfGeometryShape[] | undefined
): MapLatLng | null => {
  for (const shape of shapes ?? []) {
    const contour = normalizeContour(shape.points.filter(isValidGeometryPoint).map(toMapPoint));

    if (contour.length >= 3) {
      return getCenterFromContours([contour]);
    }
  }

  return getFirstPointFromGeometryShapes(shapes);
};

const getGeoJsonPointFromCoordinates = (coordinates: unknown): CourseMapDevicePoint | null => {
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;

  return toDevicePoint(coordinates[1], coordinates[0]);
};

const getDevicePointFromRecord = (record: Record<string, unknown>): CourseMapDevicePoint | null => {
  const directPoint = toDevicePoint(
    getFirstRecordValue(record, ['lat', 'latitude', 'center_lat']),
    getFirstRecordValue(record, ['lon', 'lng', 'longitude', 'center_lon'])
  );

  if (directPoint) {
    return directPoint;
  }

  const coordinatesPoint = getGeoJsonPointFromCoordinates(record.coordinates);

  if (coordinatesPoint) {
    return coordinatesPoint;
  }

  for (const key of ['center', 'centre', 'point', 'gps', 'location', 'position']) {
    const nestedValue = record[key];

    if (isRecord(nestedValue)) {
      const nestedPoint = getDevicePointFromRecord(nestedValue);

      if (nestedPoint) {
        return nestedPoint;
      }
    }

    if (key === 'center' && Array.isArray(nestedValue)) {
      const nestedPoint = getGeoJsonPointFromCoordinates(nestedValue);

      if (nestedPoint) {
        return nestedPoint;
      }
    }
  }

  return null;
};

const getTeePointCenter = (value: unknown): CourseMapDevicePoint | null => {
  return isRecord(value) ? getDevicePointFromRecord(value) : null;
};

const getContourFromLatLngRecords = (value: unknown): MapLatLng[] | null => {
  if (!Array.isArray(value)) return null;

  const contour = value.reduce<MapLatLng[]>((acc, item) => {
    if (!isRecord(item)) return acc;

    const devicePoint = getDevicePointFromRecord(item);

    if (!devicePoint) return acc;

    acc.push(toMapPointFromDevicePoint(devicePoint));
    return acc;
  }, []);

  const normalizedContour = normalizeContour(contour);

  return normalizedContour.length >= 3 ? normalizedContour : null;
};

const getContourFromGeoJsonCoordinates = (coordinates: unknown): MapLatLng[] | null => {
  if (!Array.isArray(coordinates)) return null;

  const ring =
    Array.isArray(coordinates[0]) && Array.isArray(coordinates[0][0])
      ? coordinates[0]
      : coordinates;

  if (!Array.isArray(ring)) return null;

  const contour = ring.reduce<MapLatLng[]>((acc, coordinatesPair) => {
    const devicePoint = getGeoJsonPointFromCoordinates(coordinatesPair);

    if (!devicePoint) return acc;

    acc.push(toMapPointFromDevicePoint(devicePoint));
    return acc;
  }, []);

  const normalizedContour = normalizeContour(contour);

  return normalizedContour.length >= 3 ? normalizedContour : null;
};

const getTeePointContour = (value: unknown): MapLatLng[] | null => {
  if (!isRecord(value)) return null;

  if (isRecord(value.geometry)) {
    const geometryContour = getContourFromGeoJsonCoordinates(value.geometry.coordinates);

    if (geometryContour) {
      return geometryContour;
    }
  }

  for (const key of ['contour', 'polygon', 'points', 'coordinates']) {
    const nestedValue = value[key];
    const contour =
      key === 'coordinates'
        ? getContourFromGeoJsonCoordinates(nestedValue)
        : (getContourFromLatLngRecords(nestedValue) ??
          getContourFromGeoJsonCoordinates(nestedValue));

    if (contour) {
      return contour;
    }
  }

  return null;
};

const getTeePointItems = (teePoints: unknown): unknown[] => {
  if (Array.isArray(teePoints)) {
    return teePoints;
  }

  if (!isRecord(teePoints)) {
    return [];
  }

  for (const key of ['results', 'data', 'items', 'features']) {
    const nestedValue = teePoints[key];

    if (Array.isArray(nestedValue)) {
      return nestedValue;
    }
  }

  if (getTeePointCenter(teePoints)) {
    return [teePoints];
  }

  return Object.values(teePoints);
};

const getDevicePointFromMapPoint = (mapPoint: MapLatLng): CourseMapDevicePoint => ({
  lat: mapPoint.lat,
  lon: mapPoint.lng,
});

const getPointInsideContour = (points: MapLatLng[], contour: MapLatLng[]): MapLatLng | null => {
  return points.find((candidatePoint) => isPointInsideOrOnContour(candidatePoint, contour)) ?? null;
};

type TeePointDropZoneSource = {
  center: CourseMapDevicePoint;
  contour: MapLatLng[] | null;
};

const getTeePointDropZoneSources = (teePoints: unknown): TeePointDropZoneSource[] => {
  return getTeePointItems(teePoints).reduce<TeePointDropZoneSource[]>((acc, teePoint) => {
    const center = getTeePointCenter(teePoint);

    if (!center) return acc;

    acc.push({
      center,
      contour: getTeePointContour(teePoint),
    });

    return acc;
  }, []);
};

const getTeePointSourceByContour = (
  teePointSources: TeePointDropZoneSource[],
  contour: MapLatLng[]
): TeePointDropZoneSource | null => {
  return (
    teePointSources.find((source) =>
      isPointInsideOrOnContour(toMapPointFromDevicePoint(source.center), contour)
    ) ?? null
  );
};

export const getHoleTeePointDropZones = (hole: CourseMapIGolfHole): CourseMapTeePointDropZone[] => {
  const geometryTeeContours = extractContoursFromGeometryShapes(hole.geometry.teeboxes);
  const geometryTeeCenterPoints = extractPointsFromGeometryShapes(hole.geometry.teebox_centers);
  const teePointSources = getTeePointDropZoneSources(hole.tee_points);

  if (geometryTeeContours.length > 0) {
    const geometryDropZones = geometryTeeContours.reduce<CourseMapTeePointDropZone[]>(
      (acc, contour, index) => {
        const geometryCenterPoint = getPointInsideContour(geometryTeeCenterPoints, contour);
        const matchedTeePointSource = getTeePointSourceByContour(teePointSources, contour);
        const center = geometryCenterPoint
          ? getDevicePointFromMapPoint(geometryCenterPoint)
          : matchedTeePointSource?.center;

        if (!center) return acc;

        acc.push({
          id: `tee-point-${index}`,
          center,
          contour,
        });

        return acc;
      },
      []
    );

    if (geometryDropZones.length > 0) {
      return geometryDropZones;
    }
  }

  return teePointSources.map((teePointSource, index) => {
    return {
      id: `tee-point-${index}`,
      center: teePointSource.center,
      contour: teePointSource.contour,
    };
  });
};

export const getTeePointDropZoneByPosition = (
  teePointDropZones: CourseMapTeePointDropZone[],
  position: MapLatLng
): CourseMapTeePointDropZone | null => {
  return (
    teePointDropZones.find((zone) => {
      if (zone.contour) {
        return isPointInsideOrOnContour(position, zone.contour);
      }

      return (
        getSquaredDistance(position, toMapPointFromDevicePoint(zone.center)) <=
        TEE_POINT_CENTER_SNAP_RADIUS * TEE_POINT_CENTER_SNAP_RADIUS
      );
    }) ?? null
  );
};

const getHoleFallbackGeometryCenter = (hole: CourseMapIGolfHole): MapLatLng | null => {
  const geometry = hole.geometry;
  const allContours = [
    ...extractContoursFromGeometryShapes(geometry.green),
    ...extractContoursFromGeometryShapes(geometry.fairway),
    ...extractContoursFromGeometryShapes(geometry.teeboxes),
    ...extractContoursFromGeometryShapes(geometry.bunkers),
  ];

  return getCenterFromContours(allContours);
};

export const getHoleFieldMarkerPosition = (hole: CourseMapIGolfHole): MapLatLng | null => {
  const geometry = hole.geometry;

  const fairwayCenter = getLargestContourVisualCenterFromGeometryShapes(geometry.fairway);

  if (fairwayCenter) {
    return fairwayCenter;
  }

  const greenCenter = getLargestContourVisualCenterFromGeometryShapes(geometry.green);

  if (greenCenter) {
    return greenCenter;
  }

  const perimeterCenter = getCenterFromContours(
    extractContoursFromGeometryShapes(geometry.perimeter)
  );

  if (perimeterCenter) {
    return perimeterCenter;
  }

  return getHoleFallbackGeometryCenter(hole);
};

export const getHoleCameraMarkerPosition = (hole: CourseMapIGolfHole): MapLatLng | null => {
  const geometry = hole.geometry;

  return (
    getFirstPointFromGeometryShapes(geometry.green_center) ??
    getCenterFromGeometryShapes(geometry.green) ??
    getHoleFieldMarkerPosition(hole)
  );
};

export const getHoleDeviceMarkerPosition = (hole: CourseMapIGolfHole): MapLatLng | null => {
  const geometry = hole.geometry;
  const storedDevicePoint = hole.device_point ? toMapPointFromDevicePoint(hole.device_point) : null;
  const firstTeePointCenter = getHoleTeePointDropZones(hole)[0]?.center;

  return (
    storedDevicePoint ??
    (firstTeePointCenter ? toMapPointFromDevicePoint(firstTeePointCenter) : null) ??
    getFirstContourCenterFromGeometryShapes(geometry.teeboxes) ??
    getFirstPointFromGeometryShapes(geometry.teebox_centers) ??
    getHoleFieldMarkerPosition(hole)
  );
};

const createHoleMarker = (
  courseId: string,
  holeNumber: number,
  position: MapLatLng,
  polygonId: string
): ContourMarkerItem => ({
  id: polygonId,
  polygonId,
  size: 'medium',
  label: `#${holeNumber}`,
  icon: createElement(TeeIcon),
  position,
});

const getDefaultCenter = (course: CourseMapIGolfCourse, fallbackPoints: MapLatLng[]): MapLatLng => {
  const courseLat = toFiniteNumber(course.latitude);
  const courseLng = toFiniteNumber(course.longitude);

  if (courseLat !== null && courseLng !== null) {
    return point(courseLat, courseLng);
  }

  const bounds = getBoundsBox(fallbackPoints);

  return bounds ? getBoundsCenter(bounds) : point(0, 0);
};

export const buildCourseMapDataFromIGolfCourse = (
  course: CourseMapIGolfCourse,
  needToParseIncludesZones: boolean
): CourseMapData => {
  const courseId = course.id_course;
  const courseName = course.course_name.trim() || course.id_course;
  const contours: CourseContourPolygon[] = [];
  const zonePolygons: CourseZonePolygon[] = [];
  const markers: ContourMarkerItem[] = [];

  course.holes.forEach((hole, index) => {
    const holeNumber = Number.isFinite(hole.hole_number) ? hole.hole_number : index + 1;
    const polygonId = String(hole.id);
    const perimeterContours = extractContoursFromGeometryShapes(hole.geometry.perimeter);

    perimeterContours.forEach((contour, contourIndex) => {
      const id = `${courseId}-hole-${holeNumber}-perimeter-${contourIndex}`;

      contours.push({
        id,
        courseId,
        polygonId,
        holeNumber,
        contour,
      });

      zonePolygons.push({
        id,
        courseId,
        polygonId,
        holeNumber,
        type: CourseZonePolygonType.BACKGROUND,
        contour,
      });
    });

    // all zones inside field
    if (needToParseIncludesZones) {
      GEOMETRY_ZONE_TYPE_MAP.forEach(([zoneKey, zoneType]) => {
        extractContoursFromGeometryShapes(hole.geometry[zoneKey]).forEach(
          (contour, contourIndex) => {
            zonePolygons.push({
              id: `${courseId}-hole-${holeNumber}-${zoneType}-${contourIndex}`,
              courseId,
              polygonId,
              holeNumber,
              type: zoneType,
              contour,
            });
          }
        );
      });
    }

    const markerPosition = getHoleFieldMarkerPosition(hole);

    if (markerPosition) {
      markers.push(createHoleMarker(courseId, holeNumber, markerPosition, polygonId));
    }
  });

  const fallbackPoints = [
    ...contours.flatMap((contour) => contour.contour),
    ...zonePolygons.flatMap((polygon) => polygon.contour),
    ...markers.map((marker) => marker.position),
  ];

  return {
    courseId,
    courseName,
    sourceFileName: course.id_course,
    holesCount: course.layout_holes ?? course.holes.length,
    defaultCenter: getDefaultCenter(course, fallbackPoints),
    contours,
    zonePolygons,
    markers,
  };
};

export const buildCourseHoleMapDataFromIGolfHole = ({
  courseId,
  courseName,
  hole,
}: BuildCourseHoleMapDataParams): CourseMapData => {
  const mapData = buildCourseMapDataFromIGolfCourse(
    {
      id_course: courseId,
      course_name: courseName ?? `Hole #${hole.hole_number}`,
      layout_holes: 1,
      holes: [hole],
    },
    true
  );

  return {
    ...mapData,
    holesCount: 1,
    markers: [],
  };
};
